"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.truncate = exports.roundToIncrement = exports.countDecimals = void 0;
const react_1 = __importStar(require("react"));
const prop_types_1 = __importDefault(require("prop-types"));
const emotion_1 = require("emotion");
const Tooltip_1 = __importDefault(require("./Tooltip"));
/**
 * Determines the number of digits after the decimal place. Returns 0 if there are none.
 * @param {number} num any real number
 * @returns {number} - an integer denoting the number of decimal digits
 */
function countDecimals(num) {
    if (Math.floor(num) === num)
        return 0;
    return num.toString().split('.')[1].length;
}
exports.countDecimals = countDecimals;
/**
 * Rounds the given number to the closest increment. The increment can be any real number.
 * @param {number} num any real number
 * @param {number} inc any real number
 * @returns {number} - num rounded to the closest increment.
 */
function roundToIncrement(num, inc) {
    return Math.round(Math.round(num / (inc / 10)) / 10) * inc; // https://gordonlesti.com/inaccurate-rounding-with-decimal-digits/
}
exports.roundToIncrement = roundToIncrement;
/**
 * Set the number a string, truncate to the given decimal point, and cut off any extraneous decimal zeroes.
 * @param {number} num any real number
 * @param {number} decimals any integer
 * @returns {number} - num truncated to the decimals, plus any extra zeroes removed.
 */
function truncate(num, decimals) {
    // toFixed() solves the problem of floats (such as 1.0000000000000000012) being returned from increment.
    // the replace() will then determine if the result is a decimal with trailing zeroes, and remove the zeroes/decimal if applicable.
    return num.toFixed(decimals).replace(/\.\d*0+$/, match => match.replace(/\.?0+$/, ''));
}
exports.truncate = truncate;
let barWidth = 0;
let barLeft = 0;
let touchIdentifier = 0;
function getPosition(element) {
    const box = element.getBoundingClientRect();
    return {
        left: box.left + window.scrollX,
        top: box.top + window.scrollY,
        width: box.width,
        height: box.height,
    };
}
function getMouseX(e) {
    // Regular mouse events.
    if (typeof e.pageX === 'number' && e.pageX === e.pageX) { // eslint-disable-line no-self-compare
        return e.pageX;
    }
    // Touch events.
    for (let i = 0; i < e.changedTouches.length; i += 1) {
        if (e.changedTouches[i].identifier === touchIdentifier) {
            return e.changedTouches[i].pageX;
        }
    }
    return 0; // It should never get here.
}
const Slider = props => {
    // Destructure the props
    const { value, label, min, max, disabled, increment, selectOnDrag, onSelect, className } = props, rest = __rest(props, ["value", "label", "min", "max", "disabled", "increment", "selectOnDrag", "onSelect", "className"]);
    // State setup
    const [internalNumber, setInternalNumber] = react_1.useState(value);
    const [displayString, setDisplayString] = react_1.useState(value.toString());
    const [roundedNumber, setRoundedNumber] = react_1.useState(value);
    const [decimals, setDecimals] = react_1.useState(increment);
    const [dragging, setDragging] = react_1.useState(false);
    const [hover, setHover] = react_1.useState(false);
    // Ref to the slider bar
    const sliderBarRef = react_1.useRef(null);
    const left = `${100 * ((internalNumber - min) / (max - min))}%`;
    const shouldFocus = hover || dragging;
    /* eslint-disable react-hooks/exhaustive-deps */
    react_1.useEffect(() => {
        if (value !== roundedNumber) {
            setInternalNumber(value);
            setDisplayString(value.toString());
            setRoundedNumber(value);
        }
        setDecimals(countDecimals(increment));
    }, [value, increment]);
    react_1.useEffect(() => {
        if (!dragging) {
            onSelect(roundedNumber);
        }
    }, [dragging]);
    const onDragMove = e => {
        const mouseX = getMouseX(e);
        let nextInternalNumber;
        if (mouseX <= barLeft) {
            nextInternalNumber = min;
        }
        else if (mouseX >= barLeft + barWidth) {
            nextInternalNumber = max;
        }
        else {
            const range = max - min;
            const percentage = (mouseX - barLeft) / barWidth;
            nextInternalNumber = min + percentage * range;
        }
        const nextRoundedNumber = roundToIncrement(nextInternalNumber, increment);
        const nextDisplayString = truncate(nextRoundedNumber, decimals);
        setInternalNumber(nextInternalNumber);
        setRoundedNumber(nextRoundedNumber);
        setDisplayString(nextDisplayString);
        if (selectOnDrag && nextRoundedNumber !== value) {
            onSelect(nextRoundedNumber);
        }
    };
    const onDragEnd = () => {
        setDragging(false);
        document.removeEventListener('mousemove', onDragMove);
        document.removeEventListener('touchmove', onDragMove);
        document.removeEventListener('mouseup', onDragEnd);
        document.removeEventListener('touchend', onDragEnd);
    };
    const onDragStart = e => {
        if (!disabled) {
            const barPosition = getPosition(sliderBarRef.current);
            barLeft = barPosition.left;
            barWidth = barPosition.width;
            if (e.targetTouches) {
                touchIdentifier = e.targetTouches[0].identifier;
            }
            setDragging(true);
            document.addEventListener('mousemove', onDragMove);
            document.addEventListener('touchmove', onDragMove);
            document.addEventListener('mouseup', onDragEnd);
            document.addEventListener('touchend', onDragEnd);
            onDragMove(e);
        }
    };
    const onMouseOver = () => {
        setHover(true);
    };
    const onMouseOut = () => {
        setHover(false);
    };
    // TODO: {https://gitlab.com/Cimpress-Technology/internal-open-source/component-library/react-components/-/issues/151} fix a11y issues with this component.
    /* eslint jsx-a11y/interactive-supports-focus: 0
              jsx-a11y/no-static-element-interactions: 0
    */
    return (react_1.default.createElement("div", Object.assign({ className: emotion_1.cx('slider', { 'slider-focus': shouldFocus }, className), disabled: disabled, onMouseOver: onMouseOver, onFocus: onMouseOver, onMouseOut: onMouseOut, onBlur: onMouseOut }, rest),
        react_1.default.createElement("div", { className: "slider-title" }, label),
        react_1.default.createElement("div", { ref: sliderBarRef, className: "slider-bar", onTouchStart: onDragStart, onMouseDown: onDragStart },
            react_1.default.createElement("div", { className: "slider-progress-bar", style: { width: left } })),
        react_1.default.createElement("div", { className: "slider-handle", style: { left } },
            react_1.default.createElement(Tooltip_1.default, { direction: "bottom", show: shouldFocus, variant: "popover", contents: displayString },
                react_1.default.createElement("div", { className: "slider-virtual-handle", onMouseDown: onDragStart, onTouchStart: onDragStart }))),
        react_1.default.createElement("div", { className: "slider-values" },
            react_1.default.createElement("div", { className: "slider-min" }, min),
            react_1.default.createElement("div", { className: "slider-max" }, max))));
};
Slider.propTypes = {
    label: prop_types_1.default.string.isRequired,
    disabled: prop_types_1.default.bool,
    min: prop_types_1.default.number,
    max: prop_types_1.default.number,
    value: prop_types_1.default.oneOfType([prop_types_1.default.number, prop_types_1.default.string]).isRequired,
    onSelect: prop_types_1.default.func.isRequired,
    increment: prop_types_1.default.number,
    selectOnDrag: prop_types_1.default.bool,
    className: prop_types_1.default.string,
};
Slider.defaultProps = {
    disabled: false,
    increment: 1,
    min: 0,
    max: 100,
    selectOnDrag: false,
};
exports.default = Slider;
